import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";
import LoginPage from "@/pages/LoginPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SettingsPage from "@/pages/SettingsPage";
import AuthCallback from "@/pages/AuthCallback";
import OverallPerformancePage from "@/pages/OverallPerformancePage";
import YearlySnapshotPage from "@/pages/YearlySnapshotPage";
import ListOfIBPage from "@/pages/ListOfIBPage";
import ChannelOverviewPage from "@/pages/ChannelOverviewPage";
import PrototypeOption1 from "@/pages/PrototypeOption1";
import PrototypeOption2 from "@/pages/PrototypeOption2";
import IBManagementOverview from "@/pages/IBManagementOverview";
import ChannelProfilePage from "@/pages/ChannelProfilePage";
import ChannelPerformancePage from "@/pages/ChannelPerformancePage";
import ChannelAnalyticPage from "@/pages/ChannelAnalyticPage";
import ChannelActivityPage from "@/pages/ChannelActivityPage";
import ChannelCampaignPage from "@/pages/ChannelCampaignPage";
import DailyDataPage from "@/pages/DailyDataPage";
import DatabaseSegmentationPage from "@/pages/DatabaseSegmentationPage";
import LogPage from "@/pages/LogPage";
import ListOfCampaignPage from "@/pages/ListOfCampaignPage";
import IBDetailsPage from "@/pages/IBDetailsPage";
import MockupVerticalTabs from "@/pages/MockupVerticalTabs";
import MockupGroupedTabs from "@/pages/MockupGroupedTabs";
import MockupCompactTabs from "@/pages/MockupCompactTabs";
import MockupFeaturesPage from "@/pages/MockupFeaturesPage";
import AdminPanelPage from "@/pages/AdminPanelPage";
import ChannelSettingsPage from "@/pages/ChannelSettingsPage";
// Phase 2 - Team Engagement Pages
import MyTasksPage from "@/pages/MyTasksPage";
import IdeaParkingPage from "@/pages/IdeaParkingPage";
import MyTasksV2Page from "@/pages/MyTasksV2Page";
import ProjectManagementPage from "@/pages/ProjectManagementPage";
import TeamCalendarPage from "@/pages/TeamCalendarPage";
import MyScorecardPage from "@/pages/MyScorecardPage";
import IBManagementDashboardPage from "@/pages/IBManagementDashboardPage";
import UADashboardPage from "@/pages/UADashboardPage";
import MoodQuotesPage from "@/pages/MoodQuotesPage";
import IdeasBoardPage from "@/pages/IdeasBoardPage";
import AnonymousWallPage from "@/pages/AnonymousWallPage";
import CRMFeedbackPage from "@/pages/CRMFeedbackPage";
import CreativeRequestPage from "@/pages/CreativeRequestPage";
import CreativePerformancePage from "@/pages/CreativePerformancePage";
import TelegramTemplateGuidePage from "@/pages/TelegramTemplateGuidePage";
import VotingPollPage from "@/pages/VotingPollPage";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import StandupPage from "@/pages/StandupPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import ResourceLibraryPage from "@/pages/ResourceLibraryPage";
import IdeasFeedbackPage from "@/pages/IdeasFeedbackPage";
import FundRequestPage from "@/pages/FundRequestPage";
import MarketingFundPage from "@/pages/MarketingFundPage";
import ChannelBudgetPage from "@/pages/ChannelBudgetPage";
import TvWallPage from "@/pages/tv-wall/TvWallPage";
import ExportCenterPage from "@/pages/ExportCenterPage";
import ChangelogPage from "@/pages/ChangelogPage";
import AiAgentPage from "@/pages/AiAgentPage";
import WarRoomHQPage from "@/pages/WarRoomHQPage";
import DiagnosisCenterPage from "@/pages/DiagnosisCenterPage";
import PublicSurveyPage from "@/pages/PublicSurveyPage";
import { isSurveyHost } from "@/config/surveyDomain";
import { initSecurityProtection, initIdleLogout } from "@/utils/security";
import { WebSocketProvider } from "@/contexts/WebSocketContext";

// Initialize security protections
if (typeof window !== 'undefined') {
  initSecurityProtection();
  initIdleLogout(240);
}

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

// Handle auth callback with session_id in URL fragment
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Survey-only mode: when the app is served on tradingfeedback.com
          (or any host in SURVEY_HOSTNAMES), the ENTIRE app is scoped down to
          the public survey page — no CRM login, no CRM shell, no leaks. */}
      {isSurveyHost() ? (
        <>
          <Route path="/s/:token" element={<PublicSurveyPage mode="token" />} />
          <Route path="/" element={<PublicSurveyLanding />} />
          <Route path="/:slug" element={<PublicSurveyPage mode="slug" />} />
          <Route path="*" element={<PublicSurveyLanding />} />
        </>
      ) : (
        <>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Public CSAT survey — no auth required */}
      <Route path="/s/:token" element={<PublicSurveyPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <UADashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard-legacy" element={
        <ProtectedRoute>
          <OverallPerformancePage />
        </ProtectedRoute>
      } />
      <Route path="/ib-management" element={
        <ProtectedRoute>
          <IBManagementOverview />
        </ProtectedRoute>
      } />
      <Route path="/ib-management/overview" element={
        <ProtectedRoute>
          <IBManagementOverview />
        </ProtectedRoute>
      } />
      <Route path="/ib-management/yearly-snapshot" element={
        <ProtectedRoute>
          <YearlySnapshotPage />
        </ProtectedRoute>
      } />
      <Route path="/ib-management/list" element={
        <ProtectedRoute>
          <ListOfIBPage />
        </ProtectedRoute>
      } />
      <Route path="/ib-management/campaigns" element={
        <ProtectedRoute>
          <ListOfCampaignPage />
        </ProtectedRoute>
      } />
      <Route path="/ib-management/war-room" element={
        <ProtectedRoute>
          <WarRoomHQPage />
        </ProtectedRoute>
      } />
      <Route path="/ib-management/diagnosis" element={
        <ProtectedRoute>
          <DiagnosisCenterPage />
        </ProtectedRoute>
      } />
      <Route path="/admin-panel" element={
        <ProtectedRoute>
          <AdminPanelPage />
        </ProtectedRoute>
      } />
      {/* Phase 2 - Team Engagement Routes */}
      <Route path="/my-tasks" element={
        <ProtectedRoute>
          <MyTasksPage />
        </ProtectedRoute>
      } />
      <Route path="/idea-parking" element={
        <ProtectedRoute>
          <IdeaParkingPage />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <TeamCalendarPage />
        </ProtectedRoute>
      } />
      <Route path="/projects" element={
        <ProtectedRoute>
          <ProjectManagementPage />
        </ProtectedRoute>
      } />
      <Route path="/my-tasks-v2" element={
        <ProtectedRoute>
          <MyTasksV2Page />
        </ProtectedRoute>
      } />
      <Route path="/my-scorecard" element={
        <ProtectedRoute>
          <MyScorecardPage />
        </ProtectedRoute>
      } />
      <Route path="/ib-management/dashboard" element={
        <ProtectedRoute>
          <IBManagementDashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/team/mood" element={
        <ProtectedRoute>
          <MoodQuotesPage />
        </ProtectedRoute>
      } />
      <Route path="/team/ideas-feedback" element={
        <ProtectedRoute>
          <IdeasFeedbackPage />
        </ProtectedRoute>
      } />
      <Route path="/team/ideas" element={
        <ProtectedRoute>
          <IdeasBoardPage />
        </ProtectedRoute>
      } />
      <Route path="/team/anonymous" element={
        <ProtectedRoute>
          <AnonymousWallPage />
        </ProtectedRoute>
      } />
      <Route path="/team/crm-feedback" element={
        <ProtectedRoute>
          <CRMFeedbackPage />
        </ProtectedRoute>
      } />
      <Route path="/team/creative-request" element={
        <ProtectedRoute>
          <CreativeRequestPage />
        </ProtectedRoute>
      } />
      <Route path="/team/creative-performance" element={
        <ProtectedRoute>
          <CreativePerformancePage />
        </ProtectedRoute>
      } />
      <Route path="/telegram-template-guide" element={
        <ProtectedRoute>
          <TelegramTemplateGuidePage />
        </ProtectedRoute>
      } />
      <Route path="/team/voting" element={
        <ProtectedRoute>
          <VotingPollPage />
        </ProtectedRoute>
      } />
      <Route path="/team/announcements" element={
        <ProtectedRoute>
          <AnnouncementsPage />
        </ProtectedRoute>
      } />
      <Route path="/team/standup" element={
        <ProtectedRoute>
          <StandupPage />
        </ProtectedRoute>
      } />
      <Route path="/team/leaderboard" element={
        <ProtectedRoute>
          <LeaderboardPage />
        </ProtectedRoute>
      } />
      <Route path="/resources" element={
        <ProtectedRoute>
          <ResourceLibraryPage />
        </ProtectedRoute>
      } />
      <Route path="/finance/fund-request" element={
        <ProtectedRoute>
          <FundRequestPage />
        </ProtectedRoute>
      } />
      <Route path="/finance/marketing-fund" element={
        <ProtectedRoute>
          <MarketingFundPage />
        </ProtectedRoute>
      } />
      <Route path="/finance/channel-budget" element={
        <ProtectedRoute>
          <ChannelBudgetPage />
        </ProtectedRoute>
      } />
      <Route path="/tv-wall" element={<ProtectedRoute><TvWallPage /></ProtectedRoute>} />
      <Route path="/settings/export" element={
        <ProtectedRoute>
          <ExportCenterPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/changelog" element={
        <ProtectedRoute>
          <ChangelogPage />
        </ProtectedRoute>
      } />
      <Route path="/ib/:ibId/overview" element={
        <ProtectedRoute>
          <ChannelOverviewPage />
        </ProtectedRoute>
      } />
      <Route path="/ib/:ibId/profile" element={
        <ProtectedRoute>
          <ChannelProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/ib/:ibId/performance" element={
        <ProtectedRoute>
          <ChannelPerformancePage />
        </ProtectedRoute>
      } />
      <Route path="/ib/:ibId/analytics" element={
        <ProtectedRoute>
          <ChannelAnalyticPage />
        </ProtectedRoute>
      } />
      <Route path="/ib/:ibId/activities" element={
        <ProtectedRoute>
          <ChannelActivityPage />
        </ProtectedRoute>
      } />
      <Route path="/ib/:ibId/campaign" element={
        <ProtectedRoute>
          <ChannelCampaignPage />
        </ProtectedRoute>
      } />
      <Route path="/ib/:ibId/daily-data" element={
        <ProtectedRoute>
          <DailyDataPage />
        </ProtectedRoute>
      } />
      <Route path="/ib/:ibId/database" element={
        <ProtectedRoute>
          <DatabaseSegmentationPage />
        </ProtectedRoute>
      } />
      <Route path="/ib/:ibId/logs" element={
        <ProtectedRoute>
          <LogPage />
        </ProtectedRoute>
      } />
      <Route path="/ib/:ibId/details" element={
        <ProtectedRoute>
          <IBDetailsPage />
        </ProtectedRoute>
      } />
      <Route path="/ib/:ibId/settings" element={
        <ProtectedRoute>
          <ChannelSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/ai-agent" element={
        <ProtectedRoute>
          <AiAgentPage />
        </ProtectedRoute>
      } />
      <Route path="/prototype/option1" element={<PrototypeOption1 />} />
      <Route path="/prototype/option2" element={<PrototypeOption2 />} />
      <Route path="/mockups/vertical" element={<MockupVerticalTabs />} />
      <Route path="/mockups/grouped" element={<MockupGroupedTabs />} />
      <Route path="/mockups/compact" element={<MockupCompactTabs />} />
      <Route path="/mockups/features" element={<MockupFeaturesPage />} />
      <Route path="/" element={(() => {
        const token = localStorage.getItem('token');
        if (!token) return <Navigate to="/login" replace />;
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const perms = user.permissions || {};
          if (perms.channel_access === 'specific' && perms.allowed_channels?.length > 0) {
            return <Navigate to={`/ib/${perms.allowed_channels[0]}/details`} replace />;
          }
        } catch {}
        return <Navigate to="/my-scorecard" replace />;
      })()} />
      <Route path="*" element={<Navigate to="/login" replace />} />
      </>
      )}
    </Routes>
  );
}

// Landing page shown when someone visits the survey domain without a valid /s/:token
function PublicSurveyLanding() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 md:p-10 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/></svg>
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Survey Portal</h1>
        <p className="text-slate-600 text-sm">
          Please open the survey link exactly as it was shared with you.<br/>
          If you cannot find the link, contact your channel admin for a new one.
        </p>
      </div>
    </div>
  );
}

// Theme-aware Toaster component
function ThemedToaster() {
  const { isDark } = useTheme();
  return (
    <Toaster 
      position="top-right" 
      theme={isDark ? "dark" : "light"}
      closeButton
      duration={3000}
      toastOptions={{
        style: isDark ? {
          background: '#1C1C1F',
          border: '1px solid #2A2A2E',
          color: '#F8F9FA',
        } : {
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
          color: '#1F2937',
        },
      }}
    />
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <WebSocketProvider>
          <div className="App">
            <ThemedToaster />
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
          </div>
        </WebSocketProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;